/**
 * AuthenticationScreen Component
 * 
 * Multi-step authentication flow for outlet and role selection.
 * Steps: Outlet Selection → Role Selection → PIN Entry → Authentication
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';
import { getEffectiveApiBaseUrl } from '../../lib/config/terminal-urls';
import type { DeviceRole } from '../../lib/auth/storage';
import { OutletSelector } from './components/OutletSelector';
import { RoleSelector } from './components/RoleSelector';
import { PINInput } from './components/PINInput';
import { CashierCredentialsInput } from './components/CashierCredentialsInput';
import { usePINRateLimit } from './components/usePINRateLimit';

type AuthStep = 'outlet' | 'role' | 'pin';

interface SelectedOutletInfo {
  outletId: string;
  name: string;
  location?: string;
}

export function AuthenticationScreen() {
  const navigate = useNavigate();
  const { authenticate, authenticateCashier, isAuthenticated } = useOutletAuth();
  
  // Initialize API client inside component
  const [apiClient] = useState(() =>
    createPhotoBoothAPI({
      baseUrl: getEffectiveApiBaseUrl(),
    })
  );
  
  // Step state
  const [currentStep, setCurrentStep] = useState<AuthStep>('outlet');
  const [selectedOutlet, setSelectedOutlet] = useState<SelectedOutletInfo | null>(null);
  const [selectedRole, setSelectedRole] = useState<DeviceRole | null>(null);
  
  // Authentication state
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Offline state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // PIN rate limiting
  const {
    isLocked,
    lockTimeRemaining,
    recordFailure,
    resetAttempts,
  } = usePINRateLimit();

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /**
   * Handle outlet selection
   */
  const handleOutletSelect = (outlet: SelectedOutletInfo) => {
    setSelectedOutlet(outlet);
    setCurrentStep('role');
    setAuthError(null);
  };

  /**
   * Handle role selection
   */
  const handleRoleSelect = (role: DeviceRole) => {
    setSelectedRole(role);
    setCurrentStep('pin');
    setAuthError(null);
  };

  /**
   * Handle PIN submission and authentication
   */
  const handlePINSubmit = async (pin: string) => {
    if (!selectedOutlet || !selectedRole) {
      setAuthError('Please select outlet and role first');
      return;
    }

    if (isLocked) {
      setAuthError('Too many failed attempts. Please wait.');
      return;
    }

    // Block initial authentication when offline
    if (isOffline) {
      setAuthError('Online connection required for authentication. Please connect to the internet and try again.');
      return;
    }

    try {
      setIsAuthenticating(true);
      setAuthError(null);

      // Call authenticate from context with role parameter
      await authenticate(selectedOutlet.outletId, selectedRole, pin);

      // Reset rate limiting on success
      resetAttempts();

      // Navigate to role-specific route
      navigateToRoleHome(selectedRole);
    } catch (error) {
      console.error('Authentication error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      setAuthError(errorMessage);
      
      // Record failed attempt for rate limiting
      recordFailure();
    } finally {
      setIsAuthenticating(false);
    }
  };

  /**
   * Handle cashier credentials submission and authentication
   */
  const handleCashierSubmit = async (username: string, password: string) => {
    if (!selectedOutlet) {
      setAuthError('Please select outlet first');
      return;
    }

    // Block initial authentication when offline
    if (isOffline) {
      setAuthError('Online connection required for authentication. Please connect to the internet and try again.');
      return;
    }

    try {
      setIsAuthenticating(true);
      setAuthError(null);

      // Authenticate cashier via OutletAuthProvider so global auth state is updated
      await authenticateCashier(selectedOutlet.outletId, username, password);
      
      // Navigate to cashier dashboard
      navigateToRoleHome('cashier');
    } catch (error) {
      console.error('Cashier authentication error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Cashier authentication failed';
      setAuthError(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  };

  /**
   * Navigate to role-specific home screen
   */
  const navigateToRoleHome = (role: DeviceRole) => {
    switch (role) {
      case 'customer':
        navigate('/', { replace: true });
        break;
      case 'capture':
        navigate('/capture', { replace: true });
        break;
      case 'cashier':
        navigate('/cashier', { replace: true });
        break;
      default:
        navigate('/', { replace: true });
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    setAuthError(null);
    
    if (currentStep === 'pin') {
      setCurrentStep('role');
      setSelectedRole(null);
    } else if (currentStep === 'role') {
      setCurrentStep('outlet');
      setSelectedOutlet(null);
    }
  };

  /**
   * Render offline message for initial authentication
   */
  const renderOfflineMessage = () => {
    if (!isOffline) return null;

    return (
      <div className="w-full max-w-2xl mx-auto mb-6">
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 text-center">
          <div className="text-4xl mb-3">📡</div>
          <h3 className="text-lg font-bold text-yellow-800 mb-2">Offline Mode</h3>
          <p className="text-yellow-700 text-sm">
            Online connection required for initial authentication.
            Please connect to the internet and try again.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4 py-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">📸</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Photo Booth Terminal
          </h1>
          <p className="text-gray-600">
            Authenticate this device with an outlet and role
          </p>
        </div>

        {/* Offline Message */}
        {renderOfflineMessage()}

        {/* Step Indicator */}
        <div className="flex justify-center items-center gap-4 mb-6">
          <div className={`flex items-center gap-2 ${currentStep === 'outlet' ? 'text-purple-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
              currentStep === 'outlet' ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              1
            </div>
            <span className="font-semibold hidden sm:inline">Outlet</span>
          </div>
          
          <div className="w-12 h-0.5 bg-gray-300" />
          
          <div className={`flex items-center gap-2 ${currentStep === 'role' ? 'text-purple-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
              currentStep === 'role' ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              2
            </div>
            <span className="font-semibold hidden sm:inline">Role</span>
          </div>
          
          <div className="w-12 h-0.5 bg-gray-300" />
          
          <div className={`flex items-center gap-2 ${currentStep === 'pin' ? 'text-purple-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
              currentStep === 'pin' ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              3
            </div>
            <span className="font-semibold hidden sm:inline">PIN</span>
          </div>
        </div>

        {/* Selected outlet summary on PIN step */}
        {currentStep === 'pin' && selectedOutlet && (
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-4 py-2 text-sm text-purple-700">
              <span className="text-xs font-semibold uppercase tracking-wide text-purple-500">
                Outlet
              </span>
              <span className="font-semibold truncate max-w-[200px] sm:max-w-xs">
                {selectedOutlet.name}
              </span>
              {selectedOutlet.location && (
                <span className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-[160px]">
                  ({selectedOutlet.location})
                </span>
              )}
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-3xl shadow-2xl p-6">
          {currentStep === 'outlet' && (
            <OutletSelector
              onSelect={handleOutletSelect}
              apiClient={apiClient}
            />
          )}

          {currentStep === 'role' && (
            <RoleSelector onSelect={handleRoleSelect} />
          )}

          {currentStep === 'pin' && selectedRole && (
            <>
              {/* Show PINInput for customer and capture roles */}
              {(selectedRole === 'customer' || selectedRole === 'capture') && (
                <PINInput
                  onSubmit={handlePINSubmit}
                  isLocked={isLocked}
                  lockTimeRemaining={lockTimeRemaining}
                  error={authError}
                  isLoading={isAuthenticating}
                />
              )}

              {/* Show CashierCredentialsInput for cashier role */}
              {selectedRole === 'cashier' && (
                <CashierCredentialsInput
                  onSubmit={handleCashierSubmit}
                  error={authError}
                  isLoading={isAuthenticating}
                />
              )}
            </>
          )}

          {/* Back Button */}
          {currentStep !== 'outlet' && (
            <div className="mt-6 text-center">
              <button
                onClick={handleBack}
                disabled={isAuthenticating}
                className="inline-flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-800 font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back</span>
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>Need help? Contact your system administrator</p>
        </div>
      </div>
    </div>
  );
}
